import React, { useState, useMemo, useCallback } from 'react';
import * as XLSX from 'xlsx';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Search,
  FileSpreadsheet,
  Check,
  AlertCircle,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { FieldCompletenessEntry } from '@/services/dashboardService';
import { useDebounce } from '@/hooks/useDebounce';
import { useProductDetailPageTranslations } from '@/hooks/useProductDetailPageTranslations';
import { translateFieldName } from '@/utils/fieldTranslations';

interface FieldStatusModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fieldCompleteness: FieldCompletenessEntry[];
  productId: number;
}

export const FieldStatusModal: React.FC<FieldStatusModalProps> = ({
  open,
  onOpenChange,
  fieldCompleteness,
  productId,
}) => {
  // Get translations
  const productTranslations = useProductDetailPageTranslations();

  // Local state for the modal
  const [fieldStatusSearchTerm, setFieldStatusSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(fieldStatusSearchTerm, 300);
  const [expandedSections, setExpandedSections] = useState<string[]>(['incomplete']);

  // Function to translate field names
  const translateField = useCallback(
    (fieldName: string): string => {
      return translateFieldName(fieldName, productTranslations.getTabsText);
    },
    [productTranslations.getTabsText]
  );

  // Process field completeness data
  const filteredFields = useMemo(() => {
    if (!fieldCompleteness) return { complete: [], incomplete: [] };

    const normalizedSearchTerm = debouncedSearchTerm.toLowerCase().trim();

    const filtered = fieldCompleteness.filter(
      item =>
        !normalizedSearchTerm ||
        translateField(item.field).toLowerCase().includes(normalizedSearchTerm)
    );

    // Group by complete/incomplete status
    return {
      complete: filtered.filter(item => item.complete),
      incomplete: filtered.filter(item => !item.complete),
    };
  }, [fieldCompleteness, debouncedSearchTerm, translateField]);

  // Toggle a section's expanded state
  const toggleSection = (section: string) => {
    setExpandedSections(current =>
      current.includes(section) ? current.filter(s => s !== section) : [...current, section]
    );
  };

  // Export field status as Excel
  const exportFieldStatusExcel = () => {
    if (!fieldCompleteness) return;

    // Create Excel content with "Field Status" format
    const headers = ['Field', 'Status'];
    const rows = fieldCompleteness.map(item => [
      translateField(item.field),
      item.complete
        ? productTranslations.getProductComponentsText('fieldStatusModal.status.complete')
        : productTranslations.getProductComponentsText('fieldStatusModal.status.incomplete'),
    ]);

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Field Status');

    // Save workbook to file
    XLSX.writeFile(workbook, `product-${productId}-field-status.xlsx`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex h-full max-h-[90vh] max-w-3xl flex-col overflow-hidden"
        onSubmit={(e): void => {
          e.preventDefault();
        }}
      >
        <form onSubmit={e => e.preventDefault()} className="flex flex-1 flex-col overflow-hidden">
          <DialogHeader>
            <DialogTitle>
              {productTranslations.getProductComponentsText('fieldStatusModal.title')}
            </DialogTitle>
            <DialogDescription>
              {productTranslations.getProductComponentsText('fieldStatusModal.description')}
            </DialogDescription>
          </DialogHeader>

          <div className="my-2 flex items-center justify-between gap-4 px-1" role="search">
            <div className="relative flex-1">
              <Input
                placeholder={productTranslations.getProductComponentsText(
                  'fieldStatusModal.search.placeholder'
                )}
                value={fieldStatusSearchTerm}
                onChange={e => setFieldStatusSearchTerm(e.target.value)}
                className="pl-8"
                aria-label={productTranslations.getProductComponentsText(
                  'fieldStatusModal.search.aria'
                )}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.stopPropagation();
                  }
                }}
                form=""
              />
              <Search className="absolute left-2 top-2.5 size-4 text-muted-foreground" />
            </div>

            <Button
              variant="outline"
              size="sm"
              type="button"
              onClick={exportFieldStatusExcel}
              aria-label={productTranslations.getProductComponentsText(
                'fieldStatusModal.export.aria'
              )}
            >
              <FileSpreadsheet className="mr-2 size-4" />
              {productTranslations.getProductComponentsText('fieldStatusModal.export.button')}
            </Button>
          </div>

          <div
            className="mt-2 min-h-0 flex-1 space-y-4 overflow-y-auto pr-2"
            role="region"
            aria-label={productTranslations.getProductComponentsText('fieldStatusModal.list.aria')}
          >
            {/* Incomplete Fields Section */}
            {filteredFields.incomplete.length > 0 && (
              <div className="overflow-hidden rounded-md border">
                <button
                  type="button"
                  className={`flex w-full items-center justify-between p-3 text-left font-medium ${
                    expandedSections.includes('incomplete')
                      ? 'bg-red-50 text-red-700'
                      : 'bg-slate-50'
                  }`}
                  onClick={() => toggleSection('incomplete')}
                  aria-expanded={expandedSections.includes('incomplete')}
                  aria-controls="incomplete-fields-section"
                >
                  <div className="flex items-center">
                    <AlertCircle className="mr-2 size-4" />
                    <span>
                      {productTranslations.getProductComponentsText(
                        'fieldStatusModal.sections.incomplete'
                      )}{' '}
                      ({filteredFields.incomplete.length})
                    </span>
                  </div>
                  {expandedSections.includes('incomplete') ? (
                    <ChevronDown className="size-4" />
                  ) : (
                    <ChevronRight className="size-4" />
                  )}
                </button>

                {expandedSections.includes('incomplete') && (
                  <div id="incomplete-fields-section" className="space-y-2 p-3">
                    {filteredFields.incomplete.map((item, index) => (
                      <div
                        key={`incomplete-${index}`}
                        className="flex items-center justify-between rounded-md border border-red-200 bg-red-50 p-3"
                      >
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-red-700">
                            {translateField(item.field)}
                          </span>
                          <span className="text-xs text-red-500">
                            {productTranslations.getCompletenessDrilldownText('labels.weight')}:{' '}
                            {item.weight}
                          </span>
                        </div>
                        <Badge
                          variant="destructive"
                          className="border-red-600/30 bg-red-600/10 text-red-700"
                        >
                          <AlertCircle className="mr-1 size-3" />{' '}
                          {productTranslations.getProductComponentsText(
                            'fieldStatusModal.status.incomplete'
                          )}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Complete Fields Section */}
            {filteredFields.complete.length > 0 && (
              <div className="overflow-hidden rounded-md border">
                <button
                  type="button"
                  className={`flex w-full items-center justify-between p-3 text-left font-medium ${
                    expandedSections.includes('complete')
                      ? 'bg-green-50 text-green-700'
                      : 'bg-slate-50'
                  }`}
                  onClick={() => toggleSection('complete')}
                  aria-expanded={expandedSections.includes('complete')}
                  aria-controls="complete-fields-section"
                >
                  <div className="flex items-center">
                    <Check className="mr-2 size-4" />
                    <span>
                      {productTranslations.getProductComponentsText(
                        'fieldStatusModal.sections.complete'
                      )}{' '}
                      ({filteredFields.complete.length})
                    </span>
                  </div>
                  {expandedSections.includes('complete') ? (
                    <ChevronDown className="size-4" />
                  ) : (
                    <ChevronRight className="size-4" />
                  )}
                </button>

                {expandedSections.includes('complete') && (
                  <div id="complete-fields-section" className="space-y-2 p-3">
                    {filteredFields.complete.map((item, index) => (
                      <div
                        key={`complete-${index}`}
                        className="flex items-center justify-between rounded-md border border-green-200 bg-green-50 p-3"
                      >
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-green-700">
                            {translateField(item.field)}
                          </span>
                          <span className="text-xs text-green-500">
                            {productTranslations.getCompletenessDrilldownText('labels.weight')}:{' '}
                            {item.weight}
                          </span>
                        </div>
                        <Badge
                          variant="outline"
                          className="border-green-600 bg-green-100 text-green-700"
                        >
                          <Check className="mr-1 size-3" />{' '}
                          {productTranslations.getProductComponentsText(
                            'fieldStatusModal.status.complete'
                          )}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* No Results */}
            {filteredFields.complete.length === 0 && filteredFields.incomplete.length === 0 && (
              <div className="py-8 text-center">
                <Search className="mx-auto mb-2 size-10 text-muted-foreground/60" />
                <p className="text-muted-foreground">
                  {productTranslations.getProductComponentsText('fieldStatusModal.noResults')}
                </p>
              </div>
            )}
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" type="button" onClick={() => onOpenChange(false)}>
              {productTranslations.getProductComponentsText('fieldStatusModal.close')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
